<template>
  <div
    class="contacts-activity-item__outer"
    :class="{ ['expanded']: expanded, [_orientation]: true }"
  >
    <div
      class="contacts-activity-item__wrapper"
      :class="{ ['expanded']: expanded }"
    >
      <div class="left">
        <VPreviewImage
          extraClass="contacts-activity-img"
          :orientation="_orientation"
          :image="imgLink"
          :hideTitle="true"
          :withPdfDownload="!!fileUrl"
          :pdfLoading="pdfLoading"
          :pdfDownloadHandler="e => $emit('previewPdf', e, id)"
        />
      </div>
      <div class="middle">
        <div class="sec-left">
          <div class="act-item">
            <div v-if="_isPostcards" class="type-divider">
              <span class="title mw-60"> Template: </span>
              <span class="value template" :title="templateName">
                <VLink
                  v-if="templateName && templateId && !templateDeleted"
                  :label="templateName"
                  :routerLink="
                    `/${type}-templates/settings/${templateId}?type=${getNavigateParams}`
                  "
                  extraClass="contact-activity-link"
                ></VLink>
                <div v-else>{{ templateName }}</div>
              </span>
            </div>

            <div v-else>
              <span class="title mw-87"> Color: </span>
              <span class="value pa">
                {{ color ? "Yes" : "No" }}
              </span>
            </div>
          </div>
          <div class="act-item">
            <div v-if="_isPostcards" class="type-divider">
              <span class="title mw-60"> Type: </span>
              <span class="value">
                {{ templateType }}
              </span>
            </div>
            <div v-else>
              <span class="title mw-87">
                Double Sided:
              </span>
              <span class="value pa">
                {{ doubleSided ? "Yes" : "No" }}
              </span>
            </div>
          </div>
          <div class="act-item">
            <span class="title mw-60" :class="{ ['mw-87']: _isLetters }">
              Created:
            </span>
            <span class="value">
              <VTooltip
                :text="getFormattedDate(created, true, true)"
                position="up"
                noPadding
                extraClass="details-activity-tooltip activity-date"
              >
                {{ getFormattedDate(created) }}
              </VTooltip>
            </span>
          </div>
        </div>
        <div class="sec-right">
          <div class="act-item">
            <div v-if="_isPostcards" class="type-divider">
              <span class="title mw-40"> Source: </span>
              <span class="value">
                <VTooltip
                  v-if="source === 'campaign'"
                  :text="campaign_name"
                  noPadding
                  position="up"
                >
                  <VLink
                    v-if="!campaignDeleted"
                    label="Campaign"
                    :routerLink="`/campaigns/${campaignId}`"
                    extraClass="contact-activity-link"
                  ></VLink>
                  <div v-else class="source-value">{{ campaign_name }}</div>
                </VTooltip>

                <span v-else> {{ _processMailPieceSource(source) }} </span>
              </span>
            </div>
            <div v-else>
              <span class="title mw-40"> Source: </span>
              <span class="value"
                >{{ _processMailPieceSource(source) || "Webhook" }}
              </span>
            </div>
          </div>
          <div class="act-item">
            <span class="title mw-40"> Status: </span>
            <span class="value cs">
              <VTooltip
                :text="
                  getFormattedDate(activityTrackingItems[currentStatus], true)
                "
                position="up"
                noPadding
                extraClass="details-activity-tooltip"
              >
                <div class="status-value" @click="_expand">
                  <span> {{ ActivityTrackingLabelsMap[currentStatus] }} </span>
                  <i :class="`fa fa-caret-${expanded ? 'up' : 'down'}`"></i>
                </div>
              </VTooltip>
            </span>
          </div>
        </div>
      </div>
      <div class="right">
        <VContextMenu :actions="getActions(id)" />
      </div>
    </div>

    <div v-if="expanded" class="expanded-block">
      <ContactsActivityTimeline :activityItems="activityTrackingItems" />
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import moment from "moment";
import { mapActions } from "vuex";

// components
import VTooltip from "@/components/VTooltip";
import VLink from "@/components/VLink";
import VContextMenu from "@/components/VContextMenu";
import ContactsActivityTimeline from "@/components/ContactsActivityTimeline";
import VPreviewImage from "@/components/VPreviewImage";

import withCopy from "@/mixins/withCopy";
import withNavigate from "@/mixins/withNavigate";

import { capitalizeFirstLetter, processMailpieceSource } from "@/utils/util";

import { ActivityTrackingLabelsMap } from "@/constants/common";
import { getContextMenuActions } from "@/utils/table";

import menuIcon from "@/assets/images/threedots.svg";

export default Vue.component("ContactsActivityItem", {
  props: {
    id: VueTypes.string.isRequired,
    type: VueTypes.oneOf(["letters", "postcards"]).def("postcards"),

    imgLink: VueTypes.string,
    templateName: VueTypes.string.isRequired,
    templateId: VueTypes.string,
    templateType: VueTypes.string.isRequired,
    templateDeleted: VueTypes.string,
    source: VueTypes.string.isRequired,
    created: VueTypes.string.isRequired,
    currentStatus: VueTypes.string.isRequired,
    campaign_name: VueTypes.string,
    campaignDeleted: VueTypes.string,
    campaignId: VueTypes.string,
    color: VueTypes.bool,
    doubleSided: VueTypes.bool,
    expanded: VueTypes.bool.def(false),
    activityTrackingItems: VueTypes.object,
    pdfLoading: VueTypes.bool.def(false),
    fileUrl: VueTypes.string.def("")
  },

  components: {
    VTooltip,
    VLink,
    VContextMenu,
    ContactsActivityTimeline,
    VPreviewImage
  },

  mixins: [withCopy, withNavigate],

  data() {
    return {
      menuIcon,
      ActivityTrackingLabelsMap
    };
  },

  methods: {
    _expand() {
      this.$emit("expand", this.id);
    },

    _resendMailpiece(id) {
      this.resendMailpiece({
        id,
        onSuccess: () => this.toast("Resend successful!"),
        onError: () => this.toast("Resend failed", "error")
      });
    },

    _resendLetter(id) {
      this.resendLetter({
        id,
        onSuccess: () => this.toast("Resend succesful!"),
        onError: () => this.toast("Resend failed", "error")
      });
    },

    _processMailPieceSource(str) {
      return processMailpieceSource(str);
    },

    getFormattedDate(d, extended = false, short = false) {
      if (short) {
        return moment(d).format(`h:mm A z`);
      }

      return moment(d).format(`MMM D, YYYY ${extended ? "[at] h:mm A" : ""} z`);
    },

    getActions(id) {
      let contextObj = {
        copy: (e, action) => this.copy(id)
      };

      if (this.type === "postcards") {
        contextObj = {
          ...contextObj,
          resend: () => this._resendMailpiece(id)
        };
      }

      if (this.type === "letters") {
        contextObj = {
          ...contextObj,
          resend: () => this._resendLetter(id)
        };
      }

      return getContextMenuActions(contextObj);
    },

    ...mapActions("contacts", ["resendMailpiece", "resendLetter"])
  },

  computed: {
    _orientation() {
      return this._isLetters ? "vertical" : "horizontal";
    },

    _isLetters() {
      return this.type === "letters";
    },

    _isPostcards() {
      return this.type === "postcards";
    },

    getNavigateParams() {
      return this.type === "postcards" ? "postcard" : "letter";
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "contacts-activity-item";

.#{$ns} {
  &__outer {
    border: 1px solid #cacaca;
    border-radius: 4px;
    box-shadow: inset 0 0 5px rgba(255, 255, 255, 0.31);
    transition: border-color 0.15s linear;

    .expanded-block {
      background: transparent;
      height: 143px;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
    }

    &:hover {
      box-shadow: 0 4px 14px 0 rgba(87, 87, 245, 0.1);
      border-color: $primary-color;
    }

    &.expanded {
      &:hover {
        .#{$ns}__wrapper {
          border-color: $primary-color;
        }
      }
    }
  }

  &__wrapper {
    display: flex;
    position: relative;
    padding: 19px 17px;
    transition: border-color 0.15s linear;

    &.expanded {
      border-bottom: 1px solid #cacaca;
    }

    .left {
      display: flex;
      align-items: center;

      .img-wrp {
        width: 90px;
        height: 60px;
      }

      img {
        width: 100%;
        height: 100%;
      }
    }

    .middle {
      display: flex;
      align-items: flex-end;
      margin: 0 30px;
      width: 100%;
      @include textCropEllipsis();
      //for tooltip visible
      overflow: visible;

      .sec-left,
      .sec-right {
        display: flex;
        flex-direction: column;
      }

      .sec-left {
        margin-right: 35px;
      }

      .act-item {
        display: flex;
        align-items: center;
        margin-bottom: 13px;

        &:last-child {
          margin-bottom: 0;
        }

        .title {
          width: fit-content;
          margin-right: 10px;
          color: $default-color;
          display: inline-block;

          &.mw-60 {
            min-width: 60px;
          }

          &.mw-40 {
            min-width: 46px;
          }

          &.mw-87 {
            min-width: 87px;
          }

          &.mw-40,
          &.mw-60,
          &.mw-87 {
            @media screen and (max-width: 600px) {
              min-width: auto;
            }
          }
        }

        .value {
          justify-content: space-between;

          &.pa {
            position: absolute;
          }

          &.cs {
            &:hover {
              color: $primary-color;
            }
          }

          &.template {
            max-width: 100px;
            @include textCropEllipsis();
          }
        }

        .title,
        .value {
          line-height: 19px;
        }
      }

      @media screen and (max-width: 1400px) {
        margin: 0 21px;
      }

      @media screen and (min-width: 991px) and (max-width: 1300px) {
        margin: 0 10px 0 20px;
      }

      @media screen and (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;

        .sec-left {
          .act-item {
            &:last-child {
              margin-bottom: 13px;
            }
          }
        }
      }
    }

    .right {
      position: absolute;
      right: 5px;
      top: 10px;
    }

    @media screen and (max-width: $small-screen) {
      flex-direction: column;

      .left {
        margin-bottom: 20px;
      }

      .middle {
        margin: 0;
      }
    }

    & /deep/ .ball {
      top: 60%;
      right: -75%;
    }
  }
}
</style>

<style lang="scss">
.v-tooltip__wrapper.details-activity-tooltip {
  &.activity-date {
    min-width: 80px;
  }

  align-items: flex-start;

  .status-value {
    width: 100%;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;

    i {
      margin-left: 5px;
      color: #d8d8d8;
    }

    &:hover {
      &,
      i {
        color: $primary-color;
      }
    }

    @media screen and (min-width: $tablet-screen) and (max-width: $large-screen) {
      > span {
        max-width: 100px;
        @include textCropEllipsis();
      }

      > i,
      > span {
        flex: 1;
      }
    }
  }

  [tooltip][flow^="up"] {
    &::after {
      bottom: calc(100% + 5px);
    }

    &::before {
      bottom: calc(100% + 3px);
    }
  }
}

.contact-activity-link.primary {
  font-size: 14px;
  color: $secondary-text-color;

  &:hover {
    color: $primary-color;
  }
}

.contacts-activity-img {
  .image-wrapper {
    width: 90px;
    max-width: 90px;
    height: 60px;
  }

  .with-download {
    width: 100%;
  }

  &.vertical.v-preview-image {
    .image-wrapper {
      width: 55px;
      max-width: 55px;
      height: 75px;
    }
  }
}

.type-divider {
  display: flex;
  align-items: center;
}
</style>
<style lang="scss" scoped>
.source-value {
  max-width: 50px;
  @include textCropEllipsis();
}
</style>
